/**
 * SessionRunner — guided workout flow for a patient.
 *
 * Flow (Iter96):
 *   setup      → pick camera zoom (0.5x / 1x / 2x) + start-delay (3/6/10s)
 *   intro      → "Ready to move?" splash
 *   pain_before→ overlay before the first set of each exercise (Pain 0–10)
 *   active     → exercise card w/ LOCKED prescription pills (Sets/Reps/Load/Rest)
 *   set_log    → after each Set Complete: Pain After · RPE · Fatigue (0–10)
 *   rest       → 45s rest, "Skip rest" button
 *   feedback   → final session-level feeling / notes
 *   done       → results
 *   stopping   → optional early-stop reason capture
 *
 * The camera itself is handled by <RecordVideoModal /> (opened synchronously
 * on tap for Safari — same modal used in the Exercise page recorder), with
 * the Setup screen's zoom + delay flowed through as `initialZoom`/`initialDelay`.
 */
import { useEffect, useState, useRef } from "react";
import {
  Play, Pause, SkipForward, CheckCircle2, Timer, StopCircle, Video, X,
  Dumbbell, Repeat, Clock, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import useModalA11y from "@/lib/useModalA11y";
import { ExerciseVideo } from "@/lib/exerciseMedia";
import RecordVideoModal from "@/components/RecordVideoModal";

const fmt = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const STOP_REASONS = [
  { key: "pain",      label: "Pain",            hint: "Symptoms are limiting me" },
  { key: "fatigue",   label: "Fatigue",         hint: "Too tired to continue safely" },
  { key: "time",      label: "Time constraint", hint: "I need to stop and finish later" },
  { key: "technical", label: "Technical issue", hint: "Video / connection / equipment" },
  { key: "other",     label: "Other",           hint: "" },
];

const ZOOM_PRESETS = [0.5, 1, 2];
const DELAY_PRESETS = [3, 6, 10];

/* --------- tiny reusable 0-10 slider --------- */
function Slider({ label, value, onChange, testid }) {
  return (
    <div className="mb-4">
      <div className="fl-label mb-2">{label} (0–10)</div>
      <input
        type="range" min={0} max={10} value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full accent-[#f47a20]"
        data-testid={testid}
      />
      <div className="text-right text-sm text-zinc-400 mt-1">{value}/10</div>
    </div>
  );
}

/* --------- locked-prescription pill --------- */
function PxPill({ icon: Icon, label, value, testid }) {
  return (
    <div
      className="fl-card px-3 py-2 flex items-center gap-2 opacity-90"
      title="Prescribed by your clinician — read only"
      data-testid={testid}
    >
      <Icon size={14} className="text-[#f47a20]" />
      <span className="fl-label text-[10px]">{label}</span>
      <span className="text-sm font-semibold text-white ml-auto">{value}</span>
    </div>
  );
}

export default function SessionRunner({ programme, onClose, onComplete }) {
  // Never run draft/unscheduled prescriptions (e.g. a planner duplicate not yet
  // published) in the guided session.
  const exs = (programme?.exercises || []).filter((e) => !e.unscheduled);
  const [phase, setPhase] = useState("setup");
  const [idx, setIdx] = useState(0);
  const [setIx, setSetIx] = useState(0);
  const [t, setT] = useState(0);
  const [running, setRunning] = useState(true);
  const [total, setTotal] = useState(0);
  const [fb, setFb] = useState({ feedback: "ok", pain_score: 0, fatigue_score: 0, difficulty_score: 0, notes: "", session_feeling: null, session_rpe: null });
  const [redZone, setRedZone] = useState(null);
  const [skippedCount, setSkippedCount] = useState(0);
  const [stop, setStop] = useState({ reason: "other", notes: "" });

  // Setup: camera prefs picked once for the whole session.
  const [setup, setSetup] = useState({ zoom: 1, delay: 3 });

  // Per-exercise log — reset each time the patient moves to a new exercise.
  // pain_before is captured on the pain_before overlay; the rest are averaged
  // across the sets before we ship the exercise log.
  const [exLog, setExLog] = useState({ pain_before: 0, painAfterList: [], rpeList: [], fatigueList: [] });

  // Set-log form state (what the patient fills after each Set Complete).
  const [setLog, setSetLog] = useState({ pain_after: 0, rpe: 5, fatigue: 5 });

  // Recording modal.
  const [showRecorder, setShowRecorder] = useState(false);

  const sessionId = useRef(`sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const cur = exs[idx];
  const totalSets = cur?.sets || 3;
  const REST = cur?.rest_seconds || 45;

  /* -------- timers -------- */
  useEffect(() => {
    if (phase !== "active" && phase !== "rest") return;
    if (!running) return;
    const iv = setInterval(() => {
      setT((v) => v + 1);
      setTotal((v) => v + 1);
    }, 1000);
    return () => clearInterval(iv);
  }, [phase, running]);

  useEffect(() => {
    if (phase === "rest" && t >= REST) advanceAfterRest();
    // eslint-disable-next-line
  }, [t, phase]);

  /* -------- transitions -------- */
  const startSession = () => {
    setPhase("pain_before");
    setT(0);
    setIdx(0);
    setSetIx(0);
    setExLog({ pain_before: 0, painAfterList: [], rpeList: [], fatigueList: [] });
  };

  const confirmPainBefore = (v) => {
    setExLog((s) => ({ ...s, pain_before: v }));
    setPhase("active");
    setT(0);
  };

  const openSetLog = () => {
    // Snapshot the current pain_before as the default "pain_after" so patients
    // barely have to move the slider on a good set.
    setSetLog({ pain_after: exLog.pain_before, rpe: 5, fatigue: 5 });
    setRunning(false);
    setPhase("set_log");
  };

  const submitSetLog = async () => {
    // Push the set's scores into the running per-exercise arrays.
    const nextEx = {
      ...exLog,
      painAfterList: [...exLog.painAfterList, setLog.pain_after],
      rpeList:       [...exLog.rpeList,       setLog.rpe],
      fatigueList:   [...exLog.fatigueList,   setLog.fatigue],
    };
    setExLog(nextEx);

    const lastSetOfExercise = setIx + 1 >= totalSets;

    if (lastSetOfExercise) {
      // Ship the exercise log with aggregated per-set values.
      const avg = (arr) => (arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : 0);
      try {
        await api.post("/exercise-logs", {
          programme_exercise_id: cur.id,
          sets_done: totalSets,
          reps_done: cur.reps || 0,
          pain_before: nextEx.pain_before,
          pain_after:  avg(nextEx.painAfterList),
          rpe:         Math.max(...nextEx.rpeList, 0),
          session_id:  sessionId.current,
        });
      } catch { /* non-blocking */ }

      // Next exercise, or straight to session-level feedback.
      if (idx + 1 >= exs.length) {
        setPhase("feedback");
        return;
      }
      setIdx(idx + 1);
      setSetIx(0);
      // Reset per-exercise capture for the next exercise.
      setExLog({ pain_before: 0, painAfterList: [], rpeList: [], fatigueList: [] });
      setPhase("rest");
      setT(0);
      setRunning(true);
    } else {
      // More sets to do for this exercise — rest, then next set.
      setSetIx(setIx + 1);
      setPhase("rest");
      setT(0);
      setRunning(true);
    }
  };

  const advanceAfterRest = () => {
    // First set of a new exercise → prompt pain_before. Otherwise straight
    // into the next set of the same exercise.
    if (setIx === 0) {
      setPhase("pain_before");
    } else {
      setPhase("active");
    }
    setT(0);
  };

  const skipExercise = () => {
    setSkippedCount((n) => n + 1);
    if (idx + 1 >= exs.length) {
      setPhase("feedback");
      return;
    }
    setIdx(idx + 1);
    setSetIx(0);
    setExLog({ pain_before: 0, painAfterList: [], rpeList: [], fatigueList: [] });
    setPhase("pain_before");
    setT(0);
  };

  const openStop = () => { setRunning(false); setPhase("stopping"); };
  const resumeFromStop = () => { setPhase("active"); setRunning(true); };

  const confirmStop = async () => {
    try {
      await api.post("/sessions/stop", {
        session_id: sessionId.current,
        programme_id: programme.id,
        exercises_completed: idx + (phase === "feedback" ? 1 : 0),
        exercises_skipped: skippedCount,
        exercises_total: exs.length,
        duration_seconds: total,
        reason: stop.reason,
        notes: stop.notes,
      });
      toast.success("Session stopped. Your clinician has been notified.");
      setPhase("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save session.");
    }
  };

  const submitFeedback = async () => {
    try {
      const { data } = await api.post("/sessions/complete", {
        session_id: sessionId.current,
        programme_id: programme.id,
        exercises_completed: idx + (phase === "feedback" ? 1 : 0),
        exercises_total: exs.length,
        duration_seconds: total,
        feedback: fb.feedback,
        pain_score: fb.pain_score,
        fatigue_score: fb.fatigue_score,
        difficulty_score: fb.difficulty_score,
        session_rpe: fb.session_rpe,
        actual_minutes: Math.max(1, Math.round(total / 60)),
        notes: fb.notes,
        session_feeling: fb.session_feeling,
        session_feeling_comment: fb.notes || null,
      });
      if (data?.red_zone) {
        setRedZone({ message: data.red_zone_message });
      } else {
        toast.success(fb.session_feeling
          ? "Session complete — the coach will react in a moment."
          : "Session complete. Great work!");
      }
      setPhase("done");
    } catch {
      toast.error("Could not save session");
    }
  };

  const closeAndComplete = () => {
    onComplete && onComplete();
    onClose && onClose();
  };

  const dialogRef = useModalA11y({ open: true, onClose: () => onClose?.(), disableEscape: true });

  return (
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="session-title"
         className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm overflow-auto" data-testid="session-runner">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="fl-label text-[#f47a20]">Guided Session</div>
            <div id="session-title" className="fl-heading text-2xl mt-1">{programme?.title}</div>
          </div>
          <button onClick={onClose} className="fl-btn-ghost" data-testid="session-close">
            <X size={16} /> Close
          </button>
        </div>

        {/* progress bar visible after setup */}
        {phase !== "setup" && (
          <div className="fl-card p-6 mb-4">
            <div className="flex justify-between items-center text-xs text-zinc-400 mb-2">
              <span>Exercise {Math.min(idx + 1, exs.length)} / {exs.length}</span>
              <span className="flex items-center gap-1"><Timer size={12} /> Total {fmt(total)}</span>
            </div>
            <div className="w-full h-1 bg-white/10 rounded overflow-hidden">
              <div className="h-full bg-[#f47a20] transition-all"
                   style={{ width: `${((idx + setIx / totalSets) / Math.max(1, exs.length)) * 100}%` }} />
            </div>
          </div>
        )}

        {/* ---------------- SETUP ---------------- */}
        {phase === "setup" && (
          <div className="fl-card p-8" data-testid="session-setup">
            <div className="fl-label text-[#f47a20]">Session Setup</div>
            <div className="fl-heading text-3xl mt-1">Set your camera preferences</div>
            <p className="text-sm text-zinc-400 mt-2 mb-8">
              These apply to every recording in this session. You can retake individual clips anytime.
            </p>

            <div className="mb-6">
              <div className="fl-label mb-2">Camera zoom</div>
              <div className="grid grid-cols-3 gap-2">
                {ZOOM_PRESETS.map((z) => (
                  <button
                    key={z}
                    onClick={() => setSetup((s) => ({ ...s, zoom: z }))}
                    data-testid={`session-setup-zoom-${z}`}
                    className={"fl-card p-4 text-center transition-all " + (setup.zoom === z ? "border-[#f47a20]" : "hover:border-white/30")}
                    style={setup.zoom === z ? { borderColor: "#f47a20", borderWidth: 2 } : {}}
                  >
                    <div className="text-2xl font-semibold">{z}×</div>
                    <div className="text-[11px] text-zinc-400 mt-1">
                      {z === 0.5 ? "Wide (ultra)" : z === 1 ? "Standard" : "Close-up"}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <div className="fl-label mb-2">Start delay (countdown)</div>
              <div className="grid grid-cols-3 gap-2">
                {DELAY_PRESETS.map((d) => (
                  <button
                    key={d}
                    onClick={() => setSetup((s) => ({ ...s, delay: d }))}
                    data-testid={`session-setup-delay-${d}`}
                    className={"fl-card p-4 text-center transition-all " + (setup.delay === d ? "border-[#f47a20]" : "hover:border-white/30")}
                    style={setup.delay === d ? { borderColor: "#f47a20", borderWidth: 2 } : {}}
                  >
                    <div className="text-2xl font-semibold">{d}s</div>
                    <div className="text-[11px] text-zinc-400 mt-1">
                      {d === 3 ? "Quick" : d === 6 ? "Standard" : "Time to set up"}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <button
              className="fl-btn w-full text-lg py-3"
              onClick={() => setPhase("intro")}
              data-testid="session-setup-continue"
            >
              Continue <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* ---------------- INTRO ---------------- */}
        {phase === "intro" && (
          <div className="fl-card p-8 text-center">
            <div className="fl-heading text-3xl mb-3">Ready to move?</div>
            <p className="text-zinc-400 mb-6">
              {exs.length} exercises · pain check before each · quick feedback per set · finish with a session summary.
            </p>
            <button className="fl-btn text-lg px-8 py-3" onClick={startSession} data-testid="session-start-btn">
              <Play size={16} /> Start now
            </button>
          </div>
        )}

        {/* ---------------- PAIN BEFORE ---------------- */}
        {phase === "pain_before" && cur && (
          <div className="fl-card p-8" data-testid="session-pain-before">
            <div className="fl-label text-[#f47a20]">Before this exercise</div>
            <div className="fl-heading text-2xl mt-1">{cur.exercise?.name}</div>
            <p className="text-sm text-zinc-400 mt-2 mb-6">
              How is your pain <strong>right now</strong>, before starting {cur.exercise?.name}?
            </p>
            <Slider
              label="Pain before"
              value={exLog.pain_before}
              onChange={(v) => setExLog((s) => ({ ...s, pain_before: v }))}
              testid="pain-before-slider"
            />
            <button
              className="fl-btn w-full text-lg py-3 mt-4"
              onClick={() => confirmPainBefore(exLog.pain_before)}
              data-testid="pain-before-continue"
            >
              Start exercise <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* ---------------- ACTIVE ---------------- */}
        {phase === "active" && cur && (
          <div className="fl-card overflow-hidden">
            <ExerciseVideo ex={cur.exercise} className="max-h-72" />
            <div className="p-6">
              <div className="fl-heading text-2xl">{cur.exercise?.name}</div>
              <div className="text-sm text-zinc-400 mt-2">{cur.exercise?.instructions}</div>
              {(cur.exercise?.coaching_cues || []).length > 0 && (
                <ul className="text-xs text-zinc-400 list-disc pl-4 space-y-0.5 mt-3">
                  {(cur.exercise.coaching_cues || []).slice(0, 5).map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              )}

              {/* Locked prescription pills */}
              <div className="mt-6">
                <div className="fl-label mb-2 flex items-center gap-2">
                  Prescription
                  <span className="text-[10px] text-zinc-500 normal-case tracking-normal">read only · set by your clinician</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="session-locked-prescription">
                  <PxPill icon={Repeat}   label="Set"  value={`${setIx + 1}/${totalSets}`} testid="px-sets" />
                  <PxPill icon={Repeat}   label="Reps" value={cur.reps || "—"}             testid="px-reps" />
                  <PxPill icon={Dumbbell} label="Load" value={cur.load || "BW"}            testid="px-load" />
                  <PxPill icon={Clock}    label="Rest" value={`${REST}s`}                   testid="px-rest" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="fl-card p-4 text-center">
                  <div className="fl-label">Elapsed</div>
                  <div className="fl-heading text-3xl mt-1">{fmt(t)}</div>
                </div>
                <div className="fl-card p-4 text-center">
                  <div className="fl-label">Set of set</div>
                  <div className="fl-heading text-3xl text-[#f47a20] mt-1">{setIx + 1}<span className="text-white/40 text-xl">/{totalSets}</span></div>
                </div>
              </div>

              {/* Record button — opens the pro modal with Setup's zoom + delay */}
              <button
                className="fl-btn-ghost w-full mt-4 text-red-400 hover:text-red-500 flex items-center justify-center gap-2"
                onClick={() => setShowRecorder(true)}
                data-testid="session-record-open"
                title="Record this set — auto-sent to your clinician after review"
              >
                <Video size={16} /> Record this set
              </button>

              <div className="flex gap-3 mt-4 flex-wrap">
                <button className="fl-btn flex-1 min-w-[160px]" onClick={openSetLog} data-testid="session-complete-set">
                  <CheckCircle2 size={16} /> Set complete
                </button>
                <button className="fl-btn-ghost" onClick={() => setRunning(!running)}>
                  {running ? <Pause size={14} /> : <Play size={14} />} {running ? "Pause" : "Resume"}
                </button>
                <button className="fl-btn-ghost" onClick={skipExercise}>
                  <SkipForward size={14} /> Skip
                </button>
                <button
                  className="fl-btn-ghost text-red-400 hover:text-red-500"
                  onClick={openStop}
                  data-testid="session-stop-btn"
                  title="Stop this session early"
                >
                  <StopCircle size={14} /> Stop session
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---------------- SET LOG (pain_after · RPE · fatigue) ---------------- */}
        {phase === "set_log" && cur && (
          <div className="fl-card p-8" data-testid="session-set-log">
            <div className="fl-label text-[#f47a20]">Set {setIx + 1}/{totalSets} · {cur.exercise?.name}</div>
            <div className="fl-heading text-2xl mt-1">How was that set?</div>
            <p className="text-sm text-zinc-400 mt-2 mb-6">
              Log your body's response so your clinician can adjust next session.
            </p>

            <Slider label="Pain after"  value={setLog.pain_after} onChange={(v) => setSetLog((s) => ({ ...s, pain_after: v }))} testid="log-pain-after" />
            <Slider label="Effort (RPE)" value={setLog.rpe}         onChange={(v) => setSetLog((s) => ({ ...s, rpe: v }))}         testid="log-rpe" />
            <Slider label="Fatigue"      value={setLog.fatigue}     onChange={(v) => setSetLog((s) => ({ ...s, fatigue: v }))}     testid="log-fatigue" />

            <button
              className="fl-btn w-full text-lg py-3 mt-2"
              onClick={submitSetLog}
              data-testid="session-set-log-submit"
            >
              {setIx + 1 >= totalSets ? "Save & next exercise" : "Save & rest"} <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* ---------------- REST ---------------- */}
        {phase === "rest" && (
          <div className="fl-card p-10 text-center">
            <div className="fl-label text-[#f47a20]">Rest</div>
            <div className="fl-heading text-6xl mt-3">{fmt(Math.max(0, REST - t))}</div>
            <div className="text-sm text-zinc-400 mt-3">Next: {exs[idx]?.exercise?.name || "—"}</div>
            <button className="fl-btn mt-6" onClick={advanceAfterRest} data-testid="session-skip-rest">
              <SkipForward size={14} /> Skip rest
            </button>
          </div>
        )}

        {/* ---------------- STOPPING ---------------- */}
        {phase === "stopping" && (
          <div className="fl-card p-8" data-testid="stop-modal">
            <div className="fl-heading text-2xl mb-2">Are you sure you want to stop this session?</div>
            <p className="text-sm mb-6" style={{ color: "var(--fl-muted)" }}>
              Your partial progress will be saved and your clinician will be notified.
            </p>
            <div className="mb-4">
              <div className="text-xs mb-2" style={{ color: "var(--fl-muted)" }}>
                Progress: {idx} completed · {skippedCount} skipped · {fmt(total)} elapsed
              </div>
            </div>
            <div className="mb-4">
              <div className="fl-label mb-2">Reason (optional)</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {STOP_REASONS.map((r) => (
                  <button
                    key={r.key}
                    onClick={() => setStop({ ...stop, reason: r.key })}
                    data-testid={`stop-reason-${r.key}`}
                    className="fl-card p-3 text-left transition-all"
                    style={stop.reason === r.key
                      ? { borderColor: "var(--fl-orange)", borderWidth: 2, background: "color-mix(in oklab, var(--fl-orange) 6%, transparent)" }
                      : {}}
                  >
                    <div className="text-sm font-semibold" style={{ color: "var(--fl-text)" }}>{r.label}</div>
                    {r.hint && <div className="text-[11px] mt-0.5" style={{ color: "var(--fl-muted-2)" }}>{r.hint}</div>}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-6">
              <div className="fl-label mb-2">Notes for your clinician (optional)</div>
              <textarea
                className="fl-input" rows={3}
                value={stop.notes}
                onChange={(e) => setStop({ ...stop, notes: e.target.value })}
                placeholder="Anything they should know before your next session?"
                data-testid="stop-notes"
              />
            </div>
            <div className="flex gap-3 flex-wrap">
              <button className="fl-btn flex-1 text-lg py-3" onClick={confirmStop} data-testid="stop-confirm">
                <StopCircle size={16} /> Stop and notify clinician
              </button>
              <button className="fl-btn-ghost" onClick={resumeFromStop} data-testid="stop-cancel">
                Keep going
              </button>
            </div>
          </div>
        )}

        {/* ---------------- FEEDBACK ---------------- */}
        {phase === "feedback" && (
          <div className="fl-card p-8">
            <div className="fl-heading text-2xl mb-2">How was the session?</div>
            <p className="text-sm text-zinc-400 mb-6">
              Your feedback helps your clinician adjust next week and feeds directly into your AI Recovery Coach.
            </p>
            <div className="mb-6">
              <div className="fl-label mb-2">How did this feel?</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { key: "too_easy",     label: "Too easy",       emoji: "😌", legacyFb: "easy" },
                  { key: "about_right",  label: "About right",    emoji: "👌", legacyFb: "ok"   },
                  { key: "too_hard",     label: "Too hard",       emoji: "😮‍💨", legacyFb: "hard" },
                  { key: "pain_problem", label: "Pain / problem", emoji: "⚠️", legacyFb: "hard" },
                ].map((o) => (
                  <button
                    key={o.key}
                    onClick={() => setFb({ ...fb, session_feeling: o.key, feedback: o.legacyFb })}
                    data-testid={`session-feeling-${o.key}`}
                    className={"fl-card p-3 text-center text-sm transition-all " + (fb.session_feeling === o.key ? "border-[#f47a20]" : "hover:border-white/30")}
                    style={fb.session_feeling === o.key ? { borderColor: "#f47a20", borderWidth: 2 } : {}}
                  >
                    <div className="text-xl">{o.emoji}</div>
                    <div className="mt-1">{o.label}</div>
                  </button>
                ))}
              </div>
            </div>
            <Slider label="Pain during / after" value={fb.pain_score}       onChange={(v) => setFb({ ...fb, pain_score: v })}       testid="fb-pain" />
            <Slider label="Fatigue"              value={fb.fatigue_score}    onChange={(v) => setFb({ ...fb, fatigue_score: v })}    testid="fb-fatigue" />
            <Slider label="Difficulty"           value={fb.difficulty_score} onChange={(v) => setFb({ ...fb, difficulty_score: v })} testid="fb-difficulty" />
            {/* Dedicated whole-session RPE (0–10). Optional, never preselected —
                distinct from pain, readiness and per-exercise difficulty. */}
            <div className="mb-6" data-testid="fb-session-rpe-block">
              <div className="fl-label mb-1">Session effort (RPE 0–10)</div>
              <p className="text-[11px] text-zinc-500 mb-2">How hard was the <b>whole session</b> overall? 0 = rest, 10 = maximal. This is separate from pain. You can skip it.</p>
              <div className="flex flex-wrap gap-1.5">
                {Array.from({ length: 11 }).map((_, v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setFb((s) => ({ ...s, session_rpe: s.session_rpe === v ? null : v }))}
                    data-testid={`fb-session-rpe-${v}`}
                    className={`w-9 h-9 rounded-lg text-sm font-semibold border transition ${fb.session_rpe === v ? "bg-[#f47a20] text-black border-[#f47a20]" : "border-white/15 text-zinc-300 hover:border-[#f47a20]/50"}`}
                  >{v}</button>
                ))}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1.5" data-testid="fb-session-rpe-state">
                {fb.session_rpe === null ? "Not reported (optional)" : `Reported effort: ${fb.session_rpe}/10 · ~${Math.max(1, Math.round(total / 60))} min`}
              </div>
            </div>
            <div className="mb-6">
              <div className="fl-label mb-2">Notes (optional)</div>
              <textarea
                className="fl-input" rows={3}
                value={fb.notes}
                onChange={(e) => setFb({ ...fb, notes: e.target.value })}
                placeholder="Anything to flag for your clinician?"
                data-testid="fb-notes"
              />
            </div>
            <button className="fl-btn w-full text-lg py-3" onClick={submitFeedback} data-testid="fb-submit">
              <CheckCircle2 size={16} /> Complete workout
            </button>
          </div>
        )}

        {/* ---------------- DONE ---------------- */}
        {phase === "done" && (
          <div className="fl-card p-10 text-center" data-testid="session-done-card">
            {redZone ? (
              <div className="text-left" data-testid="session-red-zone">
                <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                     style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)" }}>
                  <StopCircle className="text-red-400" size={26} />
                </div>
                <div className="fl-heading text-2xl text-center">We're pausing the coach for now</div>
                <p className="text-base text-zinc-200 mt-4 leading-relaxed text-center">{redZone.message}</p>
                <p className="text-xs text-zinc-500 mt-4 text-center">
                  Please rest and log any symptom changes until your clinician gets in touch.
                </p>
              </div>
            ) : (
              <>
                <div className="text-5xl mb-4">🎉</div>
                <div className="fl-heading text-3xl">Session complete</div>
                <p className="text-zinc-400 mt-2">Duration {fmt(total)} · {exs.length} exercises</p>
                <p className="text-xs text-zinc-500 mt-3 max-w-md mx-auto">
                  Please log any symptom changes (pain, swelling, mood) in your daily check-in until your next planned session — it helps us catch problems early.
                </p>
              </>
            )}
            <button className="fl-btn mt-6" onClick={closeAndComplete} data-testid="session-done-close">Back to plan</button>
          </div>
        )}
      </div>

      {/* ---------------- Recording overlay ---------------- */}
      {showRecorder && cur && (
        <RecordVideoModal
          exercise={cur}
          initialZoom={setup.zoom}
          initialDelay={setup.delay}
          autoOpenOnMount
          contextNote={`Session "${programme?.title || ""}" · set ${setIx + 1}/${totalSets}`}
          onClose={() => setShowRecorder(false)}
          onUploaded={() => {
            toast.success("Clip sent to your clinician.");
            setShowRecorder(false);
          }}
        />
      )}
    </div>
  );
}
